import {
  Flex,
  Table,
  Image,
  TableProps,
  Button,
  Popconfirm,
  AutoComplete,
  Select,
  Popover,
  Input,
  message,
  Statistic
} from "antd";
import { observer } from "mobx-react-lite";
import { TextSort } from "../../helpers/SorterHelper";
import {
  DownOutlined,
  UpOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  StopOutlined,
  StarFilled
} from "@ant-design/icons";
import { useStore } from "../../stores/root-store-context";
import { Link } from "react-router-dom";
import { TimeReport } from "../../api/TimeReportsApiClient";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Option } from "antd/es/mentions";
import dayjs, { Dayjs } from 'dayjs'
import TimerBlock from "../../components/blocks/TimerBlock";
import { sumAllTime } from "../../helpers/timeReports";
import { formatMoney } from '../../helpers/formatMoney';
import { formatDateWithMonth } from "../../helpers/dateFormat";
import { Roles } from "../../enums/Roles";

var utc = require("dayjs/plugin/utc");

dayjs.extend(utc);

export const MyTime = observer(
  ({
    setLogUserId,
    setLogProjectId,
    showModalAdd,
    showModalEdit,
    deleteTime,
    setIsChangeUser,
    setEditTimeReport,
    timeReports,
    onRefresh, // Add this prop for parent refresh
  }: {
    setLogUserId: (id: string) => void;
    setLogProjectId: (id: string) => void;
    showModalAdd: () => void;
    showModalEdit: () => void;
    deleteTime: (id: string) => Promise<void>;
    setIsChangeUser: (status: boolean) => void;
    setEditTimeReport: (report: TimeReport) => void;
    timeReports: TimeReport[] | { log: TimeReport; payment: number }[];
    onRefresh?: () => void; // Optional callback to refresh parent data
  }) => {
    const { applicationStore, timeReportStore, employeesStore, projectsStore, favoriteProjectsStore } =
      useStore();
    const { t, i18n } = useTranslation();
    const [currentTimer, setCurrentTimer] = useState<any>();
    const [timeLog, setTimeLog] = useState<any>({ description: "" });
    const [userProjects, setUserProjects] = useState<any>([]);
    const [myPayment, setMyPayment] = useState<string>("0.00");
    const [myHours, setMyHours] = useState<string>("00:00");
    const [lastProjectId, setLastProjectId] = useState<string>("");
    const [localTimeReports, setLocalTimeReports] = useState<any[]>([]); // Local state for time reports

    // Use either passed timeReportStore.timeByMe or store reports
    const effectiveTimeReports = timeReportStore.timeByMe || localTimeReports;

    const refreshData = async () => {
      try {
        // Add a small delay to ensure backend has processed any recent changes
        await new Promise(resolve => setTimeout(resolve, 300));

        // Fetch fresh data from the server
        const reports = await timeReportStore.getAllByPeriod(true);

        // Update local state if no external timeReportStore.timeByMe prop
        if (!timeReportStore.timeByMe && reports) {
          setLocalTimeReports([...reports]); // Create new array reference to force re-render
        }

        // Refresh my time for the user
        await timeReportStore.getMyTime(applicationStore.user.id);

        // Call parent refresh if provided
        if (onRefresh) {
          onRefresh();
        }
      } catch (error) {
        console.error("Error refreshing data:", error);
        message.error("Failed to refresh time reports");
      }
    };

    const getTimer = async () => {
      timeReportStore.setLoading(true);
      const timer = await timeReportStore.getTimer(applicationStore.user.id);

      if (timer) {
        setCurrentTimer(timer);

        const projectId = timer.log ? timer.log.projectId : timer.projectId;
        setTimeLog({ ...timeLog, projectId: projectId });
      } else {
        setCurrentTimer(null);
      }

      timeReportStore.setLoading(false);
    };

    const fetchProjects = async (): Promise<void> => {
      const userId = Number(applicationStore.user?.id);
      if (!userId) return;

      const [allProjects, _] = await Promise.all([
        projectsStore.getAll(),
        favoriteProjectsStore.getMyFavorites(userId),
      ]);

      const userProjects = (allProjects ?? []).filter((project: any) => {
        const isDeveloper = project.developers?.some((user: any) => user.id === userId);
        const isResponsible = project.projectResponsibleId === userId;
        return isDeveloper || isResponsible;
      });

      const sortedProjects = userProjects.sort((a: any, b: any) => {
        const aFav = favoriteProjectsStore.ids.has(Number(a.id));
        const bFav = favoriteProjectsStore.ids.has(Number(b.id));

        return (aFav === bFav) ? 0 : aFav ? -1 : 1;
      });

      setUserProjects(sortedProjects);
    };

    const startTimer = async () => {
      if (timeLog.projectId === undefined) {
        message.error("Choose the project");
        return;
      }

      await timeReportStore.startTimer(timeLog.projectId);

      // Get the updated timer
      const timer = await timeReportStore.getTimer(applicationStore.user.id);
      setCurrentTimer(timer);

      // Refresh all data
      await refreshData();

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent("timerStarted"));
    };

    const handleTimerStopped = async () => {
      await getTimer();
      await refreshData();
    };

    const handleTimerStarted = async () => {
      await getTimer();
      await refreshData();
    };

    useEffect(() => {
      const initialize = async () => {
        await getTimer();
        await fetchProjects();
        if (!timeReportStore.timeByMe) {
          // If no external timeReportStore.timeByMe, load from store
          await refreshData();
        }
      };

      initialize();

      // Set up event listeners with proper handlers
      document.addEventListener("timerStopped", handleTimerStopped);
      document.addEventListener("timerStarted", handleTimerStarted);

      return () => {
        document.removeEventListener("timerStopped", handleTimerStopped);
        document.removeEventListener("timerStarted", handleTimerStarted);
      };
    }, []);

    useEffect(() => {
      if (applicationStore.user?.roles?.some(role => [Roles.Financier, Roles.Owner].includes(role.id))) {
        const reports = (effectiveTimeReports as { log: TimeReport; payment: number }[]).map((el) => { return el.log });
        const summTime = sumAllTime((reports as TimeReport[]));
        setMyHours(summTime);

        const amount = (effectiveTimeReports as { log: TimeReport; payment: number }[]).reduce((acc, el) => { acc += el.payment; return acc }, 0)
        setMyPayment(amount.toFixed(2));
      } else {
        const summTime = sumAllTime((effectiveTimeReports as TimeReport[]));
        setMyHours(summTime);
      }
    }, [effectiveTimeReports, timeReports, localTimeReports]);

    useEffect(() => {
      fetchProjects();
    }, [projectsStore.projects]);

    useEffect(() => {
      if (projectsStore.projects.length > 0 && effectiveTimeReports.length > 0) {
        const reversedReports = [...effectiveTimeReports].reverse();

        for (const el of reversedReports) {
          const log: TimeReport = (el as any).log ?? (el as TimeReport);

          const projectId = log.projectId;

          if (log.description?.includes("Vacation") || log.description?.includes("Sick Leave")) {
            continue;
          }
          const matchedProject = projectsStore.projects.find(project => {
            return project.id === projectId;
          });

          if (matchedProject) {
            setLastProjectId(matchedProject.id);
            break;
          }
        }
      }
    }, [projectsStore.projects, effectiveTimeReports]);

    const columns: TableProps<any>["columns"] = [
      {
        dataIndex: "title",
        key: "title",
        render: (text) => <div>{text}</div>,
      },
      Table.EXPAND_COLUMN,
      {
        title: t("Hours"),
        dataIndex: "time",
        key: "time",
        render: (date) => <div>{date}</div>,
      },
      {
        title: "",
        dataIndex: "project",
        render: (_: any, record: any) => {
          return (
            <>
              <Flex
                onClick={(e) => {
                  e.stopPropagation();
                }}
                gap={8}
                style={{ justifyContent: "end", alignItems: "center" }}
              >

                <Select
                  disabled={currentTimer}
                  onClick={() => {
                    setLogUserId(applicationStore.user.id);
                  }}
                  value={currentTimer ? (currentTimer.log ? currentTimer.log.projectId : currentTimer.projectId) : timeLog.projectId}
                  onChange={(value) => {
                    setTimeLog({ ...timeLog, projectId: value });
                  }}
                  style={{ width: "250px" }}
                >
                  {userProjects?.map((project: any, index: number) => (
                    <Option value={project.id} key={index.toString()}>
                      <Flex justify="space-between" align="center" style={{ width: '100%', padding: "0 3px" }}>
                        <span>{project.title}</span>
                        {favoriteProjectsStore.ids.has(Number(project.id)) && (
                          <StarFilled style={{ color: '#fadb14', fontSize: 16, margin: "0 2px" }} />
                        )}
                      </Flex>
                    </Option>
                  ))}
                </Select>
                {!currentTimer ?
                  <Button
                    onClick={(e) => {
                      startTimer();
                    }}
                  >
                    <PlayCircleOutlined />
                  </Button>
                  :
                  <Flex style={{ display: "flex", alignItems: "center" }}>
                    <TimerBlock onTimerStop={refreshData} />
                  </Flex>
                }

              </Flex>
            </>
          );
        },
      },
    ];

    if (applicationStore.user?.roles?.some(r => r.id === Roles.Financier || r.id === Roles.Owner)) {
      columns.splice(2, 0, {
        title: t("Amount"),
        dataIndex: "Amount",
        key: "Amount",
        render: (date) => (
          <div>
            {formatMoney(parseFloat(date), applicationStore.company.Currency)}
          </div>
        ),
      });
    }

    return (
      <Table
        columns={columns}
        dataSource={[
          {
            title: (
              <>
                <Flex align="center">
                  <Image
                    width={80}
                    height={80}
                    style={{ borderRadius: "50%", objectFit: "cover" }}
                    src={
                      applicationStore.user.photoId
                        ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${applicationStore.user.photoId}`
                        : `https://res.cloudinary.com/dnl3x07wo/image/upload/w_300/z03w7hlsy8hcoic9dlcn`
                    }
                  />
                  <Link
                    style={{ marginLeft: 20 }}
                    to={`/${applicationStore.company.Tenant}/employees/${applicationStore.user.id}`}
                  >
                    {applicationStore.user.firstName +
                      " " +
                      applicationStore.user.lastName}
                  </Link>
                </Flex>
              </>
            ),
            time: myHours,
            Amount: myPayment
          },
        ]}
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) => {
            if (expanded) {
              return (
                <Button shape="circle" onClick={(e) => onExpand(record, e)}>
                  <UpOutlined />
                </Button>
              );
            } else {
              return (
                <Button shape="circle" onClick={(e) => onExpand(record, e)}>
                  <DownOutlined />
                </Button>
              );
            }
          },
          expandRowByClick: true,
          expandedRowRender: (record, index) => {
            return (
              <ul>
                {effectiveTimeReports.map((el: TimeReport | { log: TimeReport; payment: number }, index) => {
                  const log = (applicationStore.user?.roles?.some(r => r.id === Roles.Financier || r.id === Roles.Owner)) ? (el as { log: TimeReport }).log : (el as TimeReport);
                  const project = projectsStore.projects.find(pr => pr.id == (el as TimeReport).projectId || pr.id == (el as any).log?.projectId);

                  return (
                    <li key={index}>
                      {log.totalTime && (
                        <Flex justify="space-between" align="center">
                          <div style={{ flex: "0 1 200px" }}>
                            <Link to={`/${applicationStore.company?.Tenant}/projects/${project?.id}`}>{project?.title}</Link>
                          </div>
                          <div style={{
                            flex: "1 1 200px", border: "1px solid lightgray", backgroundColor: "#FFFFFF", margin: "0 5px 5px 0", padding: 5,
                            borderRadius: 5, boxShadow: "0 0 5px rgba(0,0,0,0.1)"
                          }}>
                            {log.description || "(no description)"}
                          </div>
                          <div style={{ flex: "1 1" }}>
                            {log.totalTime.slice(0, 5)}
                            <Button
                              type="text"
                              shape="circle"
                              onClick={() => {
                                setIsChangeUser(false);
                                setEditTimeReport(log);
                                showModalEdit();
                              }}
                            >
                              <EditOutlined />
                            </Button>
                          </div>
                          <div style={{ flex: "1 1 50px" }}>
                            {formatDateWithMonth(log.endTime, t, i18n.language)}
                          </div>
                          <Popconfirm
                            title="Delete the time-report"
                            description="Are you sure to delete this time-report?"
                            okText="Yes"
                            cancelText="No"
                            onConfirm={async () => {
                              await deleteTime(log.id);
                              await refreshData();
                            }}
                          >
                            <Button danger type="text" shape="circle">
                              <DeleteOutlined />
                            </Button>
                          </Popconfirm>
                        </Flex>
                      )}
                    </li>
                  );
                })}
              </ul>
            );
          },
        }}
        rowHoverable
        footer={() => (
          <Flex justify="end">
            <Button
              type="primary"
              shape="circle"
              onClick={() => {
                setLogUserId(applicationStore.user.id);
                setLogProjectId(lastProjectId);
                showModalAdd();
              }}
              icon={<PlusOutlined />}
            />
          </Flex>
        )}
        pagination={false}
      />
    );
  }
);